import { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import SwalTopEnd from "../utils/swal_top_end";
import {
  getEvdStats,
  getApproStats,
  getTopPos,
  getCommerciauxRepartition,
  getVenteByYear,
  importDailyFile,
  importDailyDeleteFile,
} from "../services/evdService";

const REQUIRED_COLUMNS = [
  "POS_MSISDN",
  "COMMERCIAL_MSISDN",
  "COMMERCIAL_NAME",
  "VENTES_AIRTIME",
  "VENTES_BUNDLE",
  "CASH_BACKV2_AIRTIME_AMOUNT",
];

export default function useEvdDashboard() {
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [dateCapture, setDateCapture] = useState("");
  const [excelFile, setExcelFile] = useState(null);
  const [excel, setExcel] = useState(null);

  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingAppro, setLoadingAppro] = useState(false);
  const [loadingPos, setLoadingPos] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  const [TotalAirtime, setTotalAirtime] = useState(0);
  const [TotalBundle, setTotalBundle] = useState(0);
  const [bundle, setBundle] = useState(0);
  const [bundleViaMomo, setBundleViaMomo] = useState(0);
  const [TotalCashBack, setTotalCashBack] = useState(0);

  const [TotalApproDay, setTotalApproDay] = useState(0);
  const [approCash, setApproCash] = useState(0);
  const [approCredit, setApproCredit] = useState(0);
  const [tauxAppro, setTauxAppro] = useState(0);
  const [tauxVisite, setTauxVisite] = useState(0);
  const [totalVisite, setTotalVisite] = useState(0);
  const [totalAppro, setTotalAppro] = useState(0);

  const [dataSecteurAppro, setDataSecteurAppro] = useState([]);
  const [dataSecteurEvd, setDataSecteurEvd] = useState([]);
  const [recapCommercials, setRecapCommercials] = useState([]);
  const [topPos, setTopPos] = useState([]);
  const [venteSerie, setVenteSerie] = useState([]);

  const resetState = useCallback(() => {
    setTotalApproDay(0);
    setTotalAirtime(0);
    setTotalBundle(0);
    setTotalCashBack(0);
    setRecapCommercials([]);
    setDataSecteurAppro([]);
    setDataSecteurEvd([]);
    setTotalAppro(0);
    setTotalVisite(0);
    setTopPos([]);
    setApproCash(0);
    setApproCredit(0);
    setTauxAppro(0);
    setTauxVisite(0);
  }, []);

  const fetchEvdStats = useCallback(async (debut, fin) => {
    setLoadingStats(true);
    try {
      const { data } = await getEvdStats(debut, fin);
      setTotalAirtime(data.TotalAirtime);
      setTotalBundle(data.TotalBundle);
      setBundle(data.Bundle);
      setBundleViaMomo(data.BundleViaMomo);
      setTotalCashBack(data.TotalCashBack);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchApproStats = useCallback(async (debut, fin) => {
    setLoadingAppro(true);
    try {
      const { data } = await getApproStats(debut, fin);
      setTotalApproDay(data.totalApproDay);
      setApproCash(data.approCash);
      setApproCredit(data.approCredit);
      setTauxAppro(data.tauxAppro);
      setTauxVisite(data.tauxVisite);
      setTotalAppro(data.totalPdvAppro);
      setTotalVisite(data.totalPdvVisite);
    } finally {
      setLoadingAppro(false);
    }
  }, []);

  const fetchTopPos = useCallback(async (debut, fin) => {
    setLoadingPos(true);
    try {
      const { data } = await getTopPos(debut, fin);
      setTopPos(data.posTab);
    } finally {
      setLoadingPos(false);
    }
  }, []);

  const fetchCommerciauxRepartition = useCallback(async (debut, fin) => {
    try {
      const { data } = await getCommerciauxRepartition(debut, fin);
      const secteurAppro = Object.entries(data.maxSecteur).map(([name, value]) => ({ name, value }));
      const secteurEvd = Object.entries(data.maxSecteurEvd).map(([name, value]) => ({ name, value }));
      setDataSecteurAppro(secteurAppro);
      setDataSecteurEvd(secteurEvd);
    } catch {
      // silent
    }
  }, []);

  const fetchVenteByYear = useCallback(async () => {
    setVenteSerie([]);
    try {
      const { data } = await getVenteByYear();
      const labels = Object.values(data.labels);
      const transformed = labels.map((month) => ({
        month,
        evd: data.venteSerie[month] || 0,
      }));
      setVenteSerie(transformed);
    } catch {
      // silent
    }
  }, []);

  const loadDashboard = useCallback(async (debut, fin) => {
    await Promise.all([
      fetchEvdStats(debut, fin),
      fetchApproStats(debut, fin),
      fetchTopPos(debut, fin),
      fetchCommerciauxRepartition(debut, fin),
      fetchVenteByYear(),
    ]);
  }, [fetchEvdStats, fetchApproStats, fetchTopPos, fetchCommerciauxRepartition, fetchVenteByYear]);

  const handleFile = (e) => {
    const EXCEL_TYPES = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];
    const file = e.target.files[0];
    if (!file) return;
    if (!EXCEL_TYPES.includes(file.type)) {
      SwalTopEnd({ icon: "error", title: "Sélectionnez uniquement un fichier Excel" });
      setExcelFile(null);
      window.$("#file_excel").val("");
      return;
    }
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (ev) => {
      setExcelFile(ev.target.result);
      setExcel(file);
    };
  };

  const parseAndValidateExcel = () => {
    const workbook = XLSX.read(excelFile, { type: "buffer" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet);
    if (!rows.length) return { rows: null, error: "Le fichier est vide" };
    const keys = Object.keys(rows[0]);
    const missing = REQUIRED_COLUMNS.some((col) => !keys.includes(col));
    if (missing) return { rows: null, error: "Le fichier ne correspond pas au modèle" };
    return { rows, error: null };
  };

  const buildFormData = () => {
    const ts = Math.floor(Date.now() / 1000);
    const ext = excel.name.split(".").pop();
    const renamed = new File([excel], `${ts}_excel_.${ext}`, { type: excel.type });
    const fd = new FormData();
    fd.append("file", renamed);
    fd.append("capture_date", dateCapture);
    return fd;
  };

  const resetImportForm = () => {
    setExcelFile(null);
    setExcel(null);
    setDateCapture("");
    window.$("#file_excel").val("");
    window.$("#datecapture").val("");
    window.$("#exampleModalCenteContrat").modal("hide");
  };

  const doImport = useCallback(async (deleteFirst = false) => {
    setLoadingImport(true);
    const fd = buildFormData();
    try {
      const importFn = deleteFirst ? importDailyDeleteFile : importDailyFile;
      const resp = await importFn(fd);
      setLoadingImport(false);
      if (!deleteFirst && resp.data.code === 405) {
        const result = await Swal.fire({
          title: "Attention! cette donnée existe",
          text: `Cette action va supprimer les données du ${dateCapture}.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Oui, écraser !",
          cancelButtonText: "Non, conserver !",
        });
        if (result.isConfirmed) await doImport(true);
      } else {
        resetImportForm();
        loadDashboard(dateDebut, dateFin);
        Swal.fire({
          title: "Cette opération peut prendre quelques minutes...",
          html: `<b style="color:green">Le fichier est en cours de traitement, un mail vous sera envoyé une fois le traitement terminé</b>`,
          icon: "warning",
        });
      }
    } catch (err) {
      setLoadingImport(false);
      if (err?.response?.status === 500) {
        SwalTopEnd({ icon: "error", title: "Une erreur est survenue" });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excel, excelFile, dateCapture, dateDebut, dateFin]);

  const submitImport = async (e) => {
    e.preventDefault();
    if (!dateCapture) {
      SwalTopEnd({ icon: "error", title: "La date est obligatoire" });
      return;
    }
    if (!excel) return;
    const { rows, error } = parseAndValidateExcel();
    if (error) {
      SwalTopEnd({ icon: "error", title: error });
      return;
    }
    if (!rows) return;
    await doImport(false);
  };

  return {
    dateDebut, setDateDebut,
    dateFin, setDateFin,
    dateCapture, setDateCapture,
    loadingStats, loadingAppro, loadingPos, loadingImport,
    TotalAirtime, TotalBundle, bundle, bundleViaMomo, TotalCashBack,
    TotalApproDay, approCash, approCredit, tauxAppro, tauxVisite,
    totalVisite, totalAppro,
    dataSecteurAppro, dataSecteurEvd,
    recapCommercials, topPos, venteSerie,
    loadDashboard, resetState,
    handleFile, submitImport,
  };
}
